/**
 * @file fab.tsx
 *
 * MD3 Expressive Floating Action Button (FAB).
 *
 * Supports four sizes, an extended variant with animated label reveal,
 * shape morphing, a `lowered` elevation variant, and an optional
 * `FABPosition` container for absolute positioning within a layout.
 *
 * @see https://m3.material.io/components/floating-action-button/overview
 */

import { Slot } from "@radix-ui/react-slot";
import type { HTMLMotionProps } from "motion/react";
import { AnimatePresence, domMax, LazyMotion, m } from "motion/react";
import * as React from "react";
import { cn } from "../../../../lib/utils";
import { LoadingIndicator } from "../../../loading-indicator";
import { ProgressIndicator } from "../../../progress-indicator";
import { Ripple, useRippleState } from "../../../ripple";
import {
	ICON_SPAN_VARIANTS,
	SPRING_TRANSITION,
	SPRING_TRANSITION_FAST,
} from "../../../shared/constants";
import { TouchTarget } from "../../../shared/touch-target";

// ─────────────────────────────────────────────────────────────────────────────
// Design Tokens
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Per-size layout classes for the FAB container.
 * MD3 sizes: SM=40dp, MD=56dp, LG=96dp, XL=136dp.
 * Extended FABs use `w-full` + `px-*` via the caller.
 * @internal
 */
const SIZE_STYLES: Record<string, string> = {
	sm: "h-10 w-10",
	md: "h-14 w-14",
	lg: "h-24 w-24",
	xl: "h-[8.5rem] w-[8.5rem]",
};

/**
 * Per-size icon dimensions (Tailwind class + pixel value).
 * MD3 icon sizes: SM=24dp, MD=24dp, LG=32dp, XL=40dp.
 * @internal
 */
const SIZE_ICON: Record<string, { cls: string; px: number }> = {
	sm: { cls: "size-6", px: 24 },
	md: { cls: "size-6", px: 24 },
	lg: { cls: "size-8", px: 32 },
	xl: { cls: "size-10", px: 40 },
};

/**
 * Per-size label typography classes used in the extended variant.
 * @internal
 */
const SIZE_TEXT_CLASS: Record<string, string> = {
	sm: "text-sm  font-medium",
	md: "text-base font-medium",
	lg: "text-xl  font-semibold",
	xl: "text-2xl font-semibold",
};

// ─────────────────────────────────────────────────────────────────────────────
// Shape Morphing — Border Radius Map
//
// IMPORTANT: Use exact height/2 values for "round" radii to avoid the dead-zone
// artefact: CSS clips any radius > height/2 identically, so animating from
// 9999 → small value produces a jump/snap at the threshold.
// Heights: SM=40dp, MD=56dp, LG=96dp, XL=136dp
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Per-size border-radius tokens for all interaction / selection states.
 *
 * - `default`: idle pill radius (height / 2).
 * - `pressed`: compressed on `whileTap`.
 * - `extended`: radius for the extended (label-visible) state.
 * - `extended_pressed`: compressed extended state on `whileTap`.
 *
 * @internal
 */
const MORPH_RADIUS: Record<
	string,
	{
		default: number;
		pressed: number;
		extended: number;
		extended_pressed: number;
	}
> = {
	sm: { default: 12, pressed: 8, extended: 12, extended_pressed: 8 },
	md: { default: 16, pressed: 10, extended: 16, extended_pressed: 10 },
	lg: { default: 28, pressed: 20, extended: 28, extended_pressed: 20 },
	xl: { default: 40, pressed: 28, extended: 40, extended_pressed: 28 },
};

// ─────────────────────────────────────────────────────────────────────────────
// Color Roles
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Color-role Tailwind class map for each FAB color variant.
 * @internal
 */
const COLOR_CLASSES: Record<
	string,
	{ bg: string; text: string; shadow: string }
> = {
	primary: {
		bg: "bg-m3-primary-container",
		text: "text-m3-on-primary-container",
		shadow: "shadow-md",
	},
	secondary: {
		bg: "bg-m3-secondary-container",
		text: "text-m3-on-secondary-container",
		shadow: "shadow-md",
	},
	tertiary: {
		bg: "bg-m3-tertiary-container",
		text: "text-m3-on-tertiary-container",
		shadow: "shadow-md",
	},
	surface: {
		bg: "bg-m3-surface-container-high",
		text: "text-m3-primary",
		shadow: "shadow-md",
	},
};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type MotionButtonProps = Omit<HTMLMotionProps<"button">, "children" | "color">;

/**
 * Props for `FAB` Floating Action Button component.
 *
 * @remarks
 * - Ensure icon-only FABs specify `aria-label` for accessibility.
 * - In extended mode (`extended={true}`), text passed to `children` acts as the label, so `aria-label` may be omitted if children is string.
 * - `lowered` prop reduces the container shadow to MD3 "lowered" FAB specification;
 *   useful when placed on surface containers like Bottom App Bars for visual balance.
 *
 * @see https://m3.material.io/components/floating-action-button/overview
 */
export interface FABProps extends MotionButtonProps {
	/**
	 * Icon element to render — typically an Icon component.
	 * Swapped for loading spinner when `loading={true}`.
	 */
	icon: React.ReactNode;
	/**
	 * FAB size variant according to MD3 specification.
	 * - `sm`: Small (40dp) — Recommended for tight layouts / inside content.
	 * - `md`: Regular (56dp) — Standard primary action.
	 * - `lg`: Large (96dp) — High-emphasis action.
	 * - `xl`: Extra-large (136dp) — Spotlight action element.
	 * @default "md"
	 */
	size?: "sm" | "md" | "lg" | "xl";
	/**
	 * MD3 color role container style.
	 * @default "primary"
	 */
	colorStyle?: "primary" | "secondary" | "tertiary" | "surface";
	/**
	 * When `true`, expands FAB to display label text alongside icon.
	 * Width automatically expands to fit `children`.
	 * @default false
	 */
	extended?: boolean;
	/**
	 * Label content rendered when `extended={true}`.
	 * Plain string recommended.
	 */
	children?: React.ReactNode;
	/**
	 * When `true`, reduces container shadow to lowered elevation.
	 * @default false
	 */
	lowered?: boolean;
	/**
	 * When `true`, displays loading spinner and disables click interactions.
	 * @default false
	 */
	loading?: boolean;
	/**
	 * Loading spinner variant.
	 * @default "loading-indicator"
	 */
	loadingVariant?: "loading-indicator" | "circular";
	/**
	 * Controls FAB visibility in layout using scale motion animation.
	 * @default true
	 */
	visible?: boolean;
	/**
	 * Change the component to the underlying child element and merge props/behavior.
	 * Useful when wrapping in Next.js `<Link>` or custom anchor tags.
	 * @default false
	 */
	asChild?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// FABPosition — Layout Wrapper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Props for `FABPosition` layout wrapper component.
 * Position absolute wrapper to anchor FAB to a screen corner.
 *
 * @see {@link FABPosition}
 */
export interface FABPositionProps {
	/**
	 * Screen corner position for FAB.
	 * @default "bottom-right"
	 */
	position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
	/** Child elements — typically a `<FAB>` component */
	children: React.ReactNode;
	/** Additional CSS class */
	className?: string;
}

const POSITION_CLASS: Record<string, string> = {
	"bottom-right": "bottom-4 right-4 sm:bottom-6 sm:right-6",
	"bottom-left": "bottom-4 left-4 sm:bottom-6 sm:left-6",
	"top-right": "top-4 right-4 sm:top-6 sm:right-6",
	"top-left": "top-4 left-4 sm:top-6 sm:left-6",
};

/**
 * Absolute position wrapper component for `<FAB>`.
 *
 * Anchors FAB to a screen corner with responsive offset padding.
 * Requires parent element to have `position: relative`.
 *
 * @see https://m3.material.io/components/floating-action-button/guidelines
 */

export function FABPosition({
	position = "bottom-right",
	children,
	className,
}: FABPositionProps) {
	return (
		<div
			className={cn(
				"absolute z-10",
				POSITION_CLASS[position] ?? POSITION_CLASS["bottom-right"],
				className,
			)}
		>
			{children}
		</div>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// FAB Component
// ─────────────────────────────────────────────────────────────────────────────

const FABComponent = React.forwardRef<HTMLButtonElement, FABProps>(
	(
		{
			className,
			style,
			icon,
			size = "md",
			colorStyle = "primary",
			extended = false,
			children,
			lowered = false,
			loading = false,
			loadingVariant = "loading-indicator",
			visible = true,
			asChild = false,
			onClick,
			onKeyDown,
			"aria-label": ariaLabel,
			layout,
			layoutId,
			...restProps
		},
		ref,
	) => {
		const colors = COLOR_CLASSES[colorStyle] ?? COLOR_CLASSES.primary;
		const radiusConfig = MORPH_RADIUS[size] ?? MORPH_RADIUS.md;

		const animateRadius = extended
			? radiusConfig.extended
			: radiusConfig.default;
		const pressedRadius = extended
			? radiusConfig.extended_pressed
			: radiusConfig.pressed;

		const sizeIcon = SIZE_ICON[size] ?? SIZE_ICON.md;
		const iconClass = sizeIcon.cls;
		const iconPx = sizeIcon.px;

		// xs/sm share the SM token; only MD FAB (40dp) needs the touch target
		const needsTouchTarget = size === "sm";

		// ── Ripple ───────────────────────────────────────────────────────
		const { ripples, onPointerDown, removeRipple } = useRippleState({
			disabled: loading,
		});

		const handleClick = React.useCallback(
			(e: React.MouseEvent<HTMLButtonElement>) => {
				if (loading) {
					e.preventDefault();
					return;
				}
				onClick?.(e);
			},
			[loading, onClick],
		);

		const handleKeyDown = React.useCallback(
			(e: React.KeyboardEvent<HTMLButtonElement>) => {
				if (loading) return;
				if ((e.key === "Enter" || e.key === " ") && onClick) {
					e.preventDefault();
					(e.currentTarget as HTMLButtonElement).click();
				}
				onKeyDown?.(e);
			},
			[loading, onClick, onKeyDown],
		);

		const labelContent = asChild
			? (
					React.Children.only(children) as React.ReactElement<{
						children?: React.ReactNode;
					}>
				).props.children
			: children;

		const innerContent = (
			<>
				{needsTouchTarget && <TouchTarget />}

				<Ripple ripples={ripples} onRippleDone={removeRipple} />

				{/* Icon / Loading swap */}
				<AnimatePresence mode="wait" initial={false}>
					{loading ? (
						<m.span
							key="loading"
							layout="position"
							{...ICON_SPAN_VARIANTS}
							transition={SPRING_TRANSITION}
							className={cn(
								"flex items-center justify-center shrink-0",
								iconClass,
							)}
						>
							{loadingVariant === "loading-indicator" ? (
								<LoadingIndicator
									size={iconPx}
									color="currentColor"
									aria-label="Loading"
								/>
							) : (
								<ProgressIndicator
									variant="circular"
									size={iconPx}
									color="currentColor"
									trackColor="transparent"
									aria-label="Loading"
								/>
							)}
						</m.span>
					) : (
						<m.span
							key="icon"
							layout="position"
							{...ICON_SPAN_VARIANTS}
							transition={SPRING_TRANSITION}
							aria-hidden="true"
							className={cn(
								"flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full",
								iconClass,
							)}
						>
							{icon}
						</m.span>
					)}
				</AnimatePresence>

				{/* Extended label — animates in/out with the `extended` prop */}
				<AnimatePresence initial={false}>
					{extended && labelContent && (
						<m.span
							key="label"
							layout="position"
							initial={{ width: 0, opacity: 0 }}
							animate={{ width: "auto", opacity: 1 }}
							exit={{ width: 0, opacity: 0 }}
							transition={{
								opacity: { duration: 0.15, ease: "linear" },
								width: SPRING_TRANSITION,
								layout: SPRING_TRANSITION,
							}}
							className="overflow-hidden whitespace-nowrap ml-3"
						>
							{labelContent}
						</m.span>
					)}
				</AnimatePresence>
			</>
		);

		const containerClassName = cn(
			"relative shrink-0 inline-flex items-center justify-center",
			"select-none cursor-pointer overflow-hidden",
			"transition-[box-shadow,opacity,filter] duration-200",
			"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-m3-primary focus-visible:ring-offset-2",
			"disabled:pointer-events-none disabled:opacity-[0.38] disabled:shadow-none",
			colors.bg,
			colors.text,
			lowered ? "shadow-sm" : colors.shadow,
			SIZE_STYLES[size] ?? "h-14 w-14",
			extended && "w-auto pl-4 pr-6",
			SIZE_TEXT_CLASS[size],
			loading && "pointer-events-none opacity-75 cursor-not-allowed",
			className,
		);

		const computedAriaLabel =
			ariaLabel || (typeof children === "string" ? children : undefined);

		return (
			<LazyMotion features={domMax} strict>
				<AnimatePresence>
					{visible &&
						(asChild ? (
							<Slot
								ref={ref}
								aria-label={computedAriaLabel}
								aria-busy={loading || undefined}
								aria-disabled={loading || restProps.disabled}
								onClick={handleClick}
								onPointerDown={onPointerDown}
								onKeyDown={handleKeyDown}
								style={{
									...(style as React.CSSProperties),
									borderRadius: `${animateRadius}px`,
								}}
								className={containerClassName}
								{...(restProps as React.HTMLAttributes<HTMLElement>)}
							>
								{React.cloneElement(
									React.Children.only(children) as React.ReactElement<{
										children?: React.ReactNode;
									}>,
									{ children: innerContent },
								)}
							</Slot>
						) : (
							<m.button
								ref={ref}
								type="button"
								layout={layout !== undefined ? layout : true}
								layoutId={layoutId}
								aria-label={computedAriaLabel}
								aria-busy={loading || undefined}
								aria-disabled={loading || restProps.disabled}
								onClick={handleClick}
								onPointerDown={onPointerDown}
								onKeyDown={handleKeyDown}
								style={style}
								// ── Entrance / Exit (FAB visibility) ────────────────
								initial={{
									scale: 0.5,
									opacity: 0,
									borderRadius: animateRadius,
								}}
								animate={{ scale: 1, opacity: 1, borderRadius: animateRadius }}
								exit={{ scale: 0.5, opacity: 0 }}
								// ── Shape Morphing (extended toggle) ────────────────
								whileTap={{ borderRadius: pressedRadius }}
								transition={{
									borderRadius: SPRING_TRANSITION_FAST,
									scale: SPRING_TRANSITION,
									opacity: { duration: 0.25, ease: "easeOut" },
									layout: SPRING_TRANSITION,
								}}
								className={containerClassName}
								{...restProps}
							>
								{innerContent}
							</m.button>
						))}
				</AnimatePresence>
			</LazyMotion>
		);
	},
);

FABComponent.displayName = "FAB";

/**
 * Primary Floating Action Button (FAB) component adhering to MD3 Expressive.
 *
 * Supports sizes from SM to XL, multiple color roles, extended label expansion,
 * loading states, and entrance/exit scale motion animations.
 *
 * @remarks
 * - Requires `aria-label` for icon-only FABs.
 * - When `extended={true}`, string `children` is automatically used as the accessible label.
 * - Animates scale out when `visible={false}` using fluid spring animation.
 * - Combine with `FABPosition` to anchor to screen corners.
 *
 * @example
 * ```tsx
 * // Basic small icon-only FAB
 * <FAB icon={<Icon name="search" />} aria-label="Search" size="sm" />
 *
 * // Extended FAB with label
 * const [isOpen, setOpen] = React.useState(false);
 * <FAB
 *   icon={<Icon name="edit" />}
 *   extended={isOpen}
 *   onClick={() => setOpen(!isOpen)}
 * >
 *   Compose
 * </FAB>
 *
 * // Large FAB with loading state
 * <FAB
 *   icon={<Icon name="upload" />}

 *   size="lg"
 *   loading={isUploading}
 *   colorStyle="secondary"
 *   aria-label="Upload files"
 * />
 *
 * // Fixed at bottom right
 * <FABPosition position="bottom-right">
 *   <FAB icon={<Icon name="add" />} aria-label="Add item" />
 * </FABPosition>
 * ```

 *
 * @see https://m3.material.io/components/floating-action-button/overview
 */
export const FAB = React.memo(FABComponent);
